import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSave, FaTimes, FaEdit, FaTrash, FaSearch, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './AddExpense.css';

import { BASE_URL } from '../../../utils/api';

const AddExpense = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdminOnly = isSuperAdmin || user?.role === 'admin';
  const { colleges, loading: collegesLoading } = useColleges();
  
  // State for Lists
  const [expenses, setExpenses] = useState([]);
  const [heads, setHeads] = useState([]);
  const [banks, setBanks] = useState([]);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterHead, setFilterHead] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const searchRef = useRef(null);

  const [formData, setFormData] = useState({
    college: '',
    head: '',
    particular: '',
    amount: '',
    paymentMode: 'Cash',
    bank: '',
    referenceNo: '',
    expenseDate: new Date().toISOString().split('T')[0],
    image: null
  });

  // Auto-set college for clerk/collegeadmin
  useEffect(() => {
    if (!isAdminOnly && colleges.length > 0) {
      const cid = String(colleges[0]._id);
      setFilterCollege(cid);
      setFormData(prev => ({ ...prev, college: cid }));
    }
  }, [isAdminOnly, colleges]);

  // Fetch initial data
  useEffect(() => {
    fetchExpenses();
    fetchHeads();
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const { data } = await api.get('/banks', { params: { limit: 1000 } });
      setBanks(data.data || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchExpenses = async () => {
    setFetchLoading(true);
    try {
      const { data } = await api.get('/expenses');
      setExpenses(data.data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchHeads = async () => {
    try {
      // Adjust if endpoint is different (e.g., /fees/heads or /heads)
      const { data } = await api.get('/expenses/heads'); 
      setHeads(data.data || []);
    } catch (error) {
      console.error('Error fetching heads:', error);
    }
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Handle Input Changes with auto-reset logic
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Reset head if college changes
      if (name === 'college') newData.head = '';
      // Clear bank and reference number if switched to Cash
      if (name === 'paymentMode' && value === 'Cash') {
        newData.bank = '';
        newData.referenceNo = '';
      }
      return newData;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Invalid File', 'Only JPG, PNG, and HEIC images are allowed.', 'error');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const handleFilterCollegeChange = (e) => {
    setFilterCollege(e.target.value);
    setFilterHead(''); // Reset head filter when college changes
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      college: '',
      head: '',
      particular: '',
      amount: '',
      paymentMode: 'Cash',
      bank: '',
      referenceNo: '',
      expenseDate: new Date().toISOString().split('T')[0],
      image: null
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  // Edit Expense Setup
  const handleEdit = (expense) => {
    setEditingId(expense._id);
    setFormData({
      college: expense.college?._id || expense.college || '',
      head: expense.head?._id || expense.head || '',
      particular: expense.particular || '',
      amount: expense.amount || '',
      paymentMode: expense.paymentMode || 'Cash',
      bank: expense.bank?._id || expense.bank || '',
      referenceNo: expense.referenceNo || '',
      expenseDate: expense.expenseDate ? new Date(expense.expenseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      image: null
    });
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete Expense
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/expenses/${id}`);
        Swal.fire('Deleted!', 'The expense has been deleted.', 'success');
        fetchExpenses();
      } catch (error) {
        Swal.fire('Error!', error.response?.data?.message || 'Failed to delete expense.', 'error');
      }
    }
  };

  // Submit Add/Update
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.college || !formData.head || !formData.particular || !formData.amount || !formData.expenseDate) {
      return Swal.fire('Validation Error', 'Please fill all required fields (*).', 'warning');
    }

    if (!editingId && !formData.image) {
      return Swal.fire('Validation Error', 'Expense image is required.', 'warning');
    }

    if (formData.paymentMode !== 'Cash' && !formData.bank) {
      return Swal.fire('Validation Error', 'Debited Bank is required for selected Payment Mode.', 'warning');
    }

    if (formData.paymentMode !== 'Cash' && !formData.referenceNo) {
      return Swal.fire('Validation Error', 'Reference Number is required for non-cash payments.', 'warning');
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('college', formData.college);
      formDataToSend.append('head', formData.head);
      formDataToSend.append('particular', formData.particular);
      formDataToSend.append('amount', Number(formData.amount));
      formDataToSend.append('paymentMode', formData.paymentMode);
      if (formData.paymentMode !== 'Cash' && formData.bank) {
        formDataToSend.append('bank', formData.bank);
      }
      if (formData.paymentMode !== 'Cash' && formData.referenceNo) {
        formDataToSend.append('referenceNo', formData.referenceNo);
      }
      formDataToSend.append('expenseDate', formData.expenseDate);
      if (formData.image) formDataToSend.append('image', formData.image);

      if (editingId) {
        await api.put(`/expenses/${editingId}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire('Updated!', 'Expense updated successfully.', 'success');
      } else {
        await api.post('/expenses', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire('Added!', 'Expense added successfully.', 'success');
      }
      
      resetForm();
      fetchExpenses();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Something went wrong.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Dependent Dropdown Logic ────────────────────────────────────
  const isHeadInCollege = (head, collId) => {
    if (!collId) return true;
    if (Array.isArray(head.colleges)) return head.colleges.some(c => (c._id || c) === collId);
    return (head.college?._id || head.college) === collId;
  };

  const formAvailableHeads = heads.filter(h => isHeadInCollege(h, formData.college));
  const filterAvailableHeads = heads.filter(h => isHeadInCollege(h, filterCollege));

  // ── Filters & Grouping ──────────────────────────────────────────
  const displayedExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const search = searchQuery.toLowerCase();
      const particularMatch = (exp.particular || '').toLowerCase().includes(search);
      const refMatch = (exp.referenceNo || '').toLowerCase().includes(search);
      
      const matchSearch = particularMatch || refMatch;
      const matchCollege = filterCollege ? (exp.college?._id || exp.college) === filterCollege : true;
      const matchHead = filterHead ? (exp.head?._id || exp.head) === filterHead : true;
      
      return matchSearch && matchCollege && matchHead;
    });
  }, [expenses, searchQuery, filterCollege, filterHead]);

  const collegesToDisplay = useMemo(() => {
    if (filterCollege) return colleges.filter(c => c._id === filterCollege);
    return colleges.length > 0 ? colleges : [{ _id: 'default', name: 'Loading Colleges...' }];
  }, [colleges, filterCollege]);

  // Date Formatter Helper
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="add-expense-container">
      <h2 className="add-expense-title">Expense Management</h2>

      {/* ── Top Controls ── */}
      <div className="add-expense-controls">
        <div className="add-expense-search-wrapper" ref={searchRef}>
          <FaSearch className="add-expense-search-icon" />
          <input
            type="text"
            className="add-expense-search-input"
            placeholder="Search particulars, ref no..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-expense-search-dropdown">
              <ul>
                {displayedExpenses.length > 0 ? displayedExpenses.slice(0, 5).map(e => (
                  <li key={e._id} onClick={() => { setSearchQuery(e.particular); setShowSearchDropdown(false); }}>
                    {e.particular} <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>(₹{e.amount})</span>
                  </li>
                )) : (
                  <div className="add-expense-search-empty">No expenses found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        {isAdminOnly && (
          <select className="add-expense-filter-select" value={filterCollege} onChange={handleFilterCollegeChange}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="add-expense-filter-select" value={filterHead} onChange={e => setFilterHead(e.target.value)}>
          <option value="">All Heads</option>
          {filterAvailableHeads.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
        </select>

        <button className="add-expense-btn add-expense-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Expense'}
        </button>
      </div>

      {/* ── Add / Edit Inline Form ── */}
      {showAddForm && (
        <div className="add-expense-inline-form">
          <h3 className="add-expense-form-title">{editingId ? '✏️ Edit Expense Details' : '📝 Add New Expense'}</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="add-expense-form-grid">
              
              {isAdminOnly && (
                <div className="add-expense-form-group">
                  <label>College <span className="add-expense-req">*</span></label>
                  <select name="college" value={formData.college} onChange={handleInputChange} required>
                    <option value="">-- Select College --</option>
                    {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="add-expense-form-group">
                <label>Expense Head / Party <span className="add-expense-req">*</span></label>
                <select name="head" value={formData.head} onChange={handleInputChange} required disabled={!formData.college}>
                  <option value="">{formData.college ? '-- Select Head --' : '-- Select College First --'}</option>
                  {formAvailableHeads.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
                </select>
              </div>

              <div className="add-expense-form-group add-expense-col-span-2">
                <label>Particulars (Description) <span className="add-expense-req">*</span></label>
                <input type="text" name="particular" placeholder="e.g. Electricity Bill, Event Items" value={formData.particular} onChange={handleInputChange} required />
              </div>

              <div className="add-expense-form-group">
                <label>Amount (₹) <span className="add-expense-req">*</span></label>
                <input type="number" name="amount" className="add-expense-input-amount" placeholder="0" min="0" value={formData.amount} onChange={handleInputChange} required />
              </div>

              <div className="add-expense-form-group">
                <label>Payment Mode <span className="add-expense-req">*</span></label>
                <select name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} required>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Demand Draft">Demand Draft</option>
                </select>
              </div>

              {/* Conditionally Render Debited Bank if NOT Cash */}
              {formData.paymentMode !== 'Cash' && (
                <>
                  <div className="add-expense-form-group">
                    <label>Debited Bank <span className="add-expense-req">*</span></label>
                    <select 
                      name="bank" 
                      value={formData.bank} 
                      onChange={handleInputChange} 
                      required
                      style={!formData.bank ? {borderColor: '#ef4444'} : {}}
                    >
                      <option value="">-- Select Bank --</option>
                      {banks
                        .filter(b => !formData.college || b.colleges?.some(c => String(c._id || c) === String(formData.college)))
                        .map(b => (
                          <option key={b._id} value={b._id}>
                            {b.name} - {b.accountNumber}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="add-expense-form-group">
                    <label>Reference Number <span className="add-expense-req">*</span></label>
                    <input 
                      type="text" 
                      name="referenceNo" 
                      placeholder="Transaction/Cheque/UTR Number" 
                      value={formData.referenceNo} 
                      onChange={handleInputChange} 
                      required
                      style={!formData.referenceNo ? {borderColor: '#ef4444'} : {}}
                    />
                  </div>
                </>
              )}
              <div className="add-expense-form-group">
                <label>Expense Date <span className="add-expense-req">*</span></label>
                <input type="date" name="expenseDate" value={formData.expenseDate} onChange={handleInputChange} required />
              </div>

              <div className="add-expense-form-group add-expense-col-span-2">
                <label>Expense Image (JPG, PNG, HEIC) <span className="add-expense-req">*</span></label>
                <input 
                  type="file" 
                  name="image" 
                  accept=".jpg,.jpeg,.png,.heic" 
                  onChange={handleImageChange} 
                  required={!editingId}
                  className="add-expense-file-input"
                />
                {editingId && <small style={{ color: '#64748b', fontSize: '0.8rem' }}>Leave empty to keep existing image</small>}
              </div>


            </div>

            <div className="add-expense-form-buttons">
              <button type="button" className="add-expense-btn-cancel" onClick={resetForm} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="add-expense-btn-confirm" disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update Expense' : 'Save Expense'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── College-wise Grouped Tables ── */}
      <div className="add-expense-grouped-container">
        {fetchLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading expenses...</div>
        ) : (
          collegesToDisplay.map(college => {
            const collegeExpenses = displayedExpenses.filter(exp => (exp.college?._id || exp.college) === college._id);

            return (
              <div key={college._id} className="add-expense-table-group">
                <h3 className="add-expense-group-header">🏫 {college.name}</h3>
                <div className="add-expense-table-wrapper">
                  <table className="add-expense-table">
                    <thead>
                      <tr>
                        <th width="60px">S.No</th>
                        <th className="add-expense-align-left">Head / Party Name</th>
                        <th className="add-expense-align-left">Particular Name</th>
                        <th>Amount</th>
                        <th>Payment Mode</th>
                        <th>Debited Bank</th>
                        <th>Reference No</th>
                        <th>Expense Date</th>
                        <th>Image</th>
                        <th width="100px">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collegeExpenses.length === 0 ? (
                        <tr>
                          <td colSpan="10" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                            No expenses recorded for this college.
                          </td>
                        </tr>
                      ) : (
                        collegeExpenses.map((exp, index) => (
                          <React.Fragment key={exp._id}>
                            <tr className="add-expense-main-row">
                              <td>{index + 1}</td>
                              <td className="add-expense-align-left" style={{ fontWeight: 600, color: '#475569' }}>
                                {exp.head?.name || '—'}
                              </td>
                              <td className="add-expense-align-left">
                                <div className="add-expense-name-wrapper" onClick={() => setExpandedRow(expandedRow === exp._id ? null : exp._id)}>
                                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{exp.particular}</div>
                                  <button className="add-expense-expand-btn">
                                    {expandedRow === exp._id ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                </div>
                              </td>
                              <td className="add-expense-amount">
                                ₹ {Number(exp.amount || 0).toLocaleString()}
                              </td>
                              <td>
                                <span className="add-expense-badge">{exp.paymentMode || 'Cash'}</span>
                              </td>
                              <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {exp.paymentMode === 'Cash' ? '—' : (exp.bank?.name && exp.bank?.accountNumber ? `${exp.bank.name} - ${exp.bank.accountNumber}` : '—')}
                              </td>
                              <td style={{ color: '#64748b', fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 600 }}>
                                {exp.paymentMode === 'Cash' ? '—' : (exp.referenceNo || '—')}
                              </td>
                              <td style={{ fontWeight: 600, color: '#334155' }}>
                                {fmtDate(exp.expenseDate)}
                              </td>
                              <td>
                                {exp.image ? (
                                  <a 
                                    href={`${BASE_URL}/${exp.image}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="add-expense-image-link"
                                  >
                                    View
                                  </a>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                                )}
                              </td>
                              <td>
                                <div className="add-expense-action-btns">
                                  <button className="add-expense-btn-edit" onClick={() => handleEdit(exp)} title="Edit"><FaEdit /></button>
                                  <button className="add-expense-btn-delete" onClick={() => handleDelete(exp._id)} title="Delete"><FaTrash /></button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Expanded Row exclusively for Created / Updated Dates */}
                            {expandedRow === exp._id && (
                              <tr className="add-expense-expanded-row">
                                <td colSpan="10">
                                  <div className="add-expense-expanded-content">
                                    <div className="add-expense-exp-grid">
                                      <div className="add-expense-exp-item">
                                        <span className="add-expense-exp-label">Created At</span>
                                        <span className="add-expense-exp-value">{fmtDate(exp.createdAt)} {fmtTime(exp.createdAt)}</span>
                                      </div>
                                      <div className="add-expense-exp-item">
                                        <span className="add-expense-exp-label">Updated At</span>
                                        <span className="add-expense-exp-value">{fmtDate(exp.updatedAt)} {fmtTime(exp.updatedAt)}</span>
                                      </div>
                                      {exp.image && (
                                        <div className="add-expense-exp-item add-expense-image-preview">
                                          <span className="add-expense-exp-label">Expense Image</span>
                                          <img 
                                            src={`${BASE_URL}/${exp.image}`} 
                                            alt="Expense" 
                                            className="add-expense-preview-img"
                                            onClick={() => window.open(`${BASE_URL}/${exp.image}`, '_blank')}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default AddExpense;